import React, { FunctionComponent } from "react";

import ChevronDown from "@uui/ChevronDown";
import ChevronLeft from "@uui/ChevronLeft";
import ChevronRight from "@uui/ChevronRight";

import { Typography, Button, ButtonProps, Skeleton } from "@mui/material";
import styled from "@mui/material/styles/styled";

import type { Pagination } from "@app/common/hooks/usePagination";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import IconButton from "@rui/IconButton";
import Menu from "@rui/Menu";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

interface TablePaginatorProps extends Pagination {
  height?: number;
  loading?: boolean;
  pageSizes?: number[];
  showPageSizeSelector?: boolean;
}

interface ArrowButtonProps {
  disabled?: boolean;
  direction: "left" | "right";
  onClick: () => void;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100];

const ArrowButton = styled<FunctionComponent<ArrowButtonProps>>(
  ({ direction, ...props }) => (
    <IconButton {...props}>
      {direction === "left" ? <ChevronLeft /> : <ChevronRight />}
    </IconButton>
  )
)(({ theme }) => ({
  width: "32px",
  height: "100%",
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },
  borderRadius: 0,
  "&.Mui-disabled svg": {
    color: theme.palette.shades.neutral[200],
  },
}));

const PaginatorButtonContainer = styled(Row)(({ theme }) => ({
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  borderRadius: "8px",
  overflow: "hidden",
}));

const PageSizeSelector = styled<FunctionComponent<ButtonProps>>((props) => (
  <Button {...props} endIcon={<ChevronDown />} />
))(({ theme }) => ({
  border: "1px solid",
  borderRadius: "8px",
  borderColor: theme.palette.shades.neutral[100],
  color: theme.palette.shades.neutral[500],
  "& .MuiButton-endIcon": {
    marginRight: "2px",
  },
}));

const TablePaginator = ({
  currentPage,
  goToNextPage,
  goToPreviousPage,
  hasNext,
  hasPrevious,
  height = 48,
  pageSize,
  loading = false,
  numItems,
  setPageSize,
  showPageSizeSelector = false,
  pageSizes = DEFAULT_PAGE_SIZES,
}: TablePaginatorProps) => {
  const { anchorElement, open, onClose, onClick } = useAnchorElement();

  const handlePageSizeChange = (pageSize: number) => () => {
    setPageSize(pageSize);
    onClose();
  };

  const firstItemNumber = currentPage * pageSize + (numItems ? 1 : 0);
  const pageMax = (currentPage + 1) * pageSize;
  const lastItemNumber = numItems > pageMax ? pageMax : numItems;

  return (
    <Row spacing={1}>
      <PaginatorButtonContainer variant="left" sx={{ height: `${height}px` }}>
        <Row alignItems="center" sx={{ display: { xxs: "none", lg: "flex" } }}>
          {loading ? (
            <Skeleton variant="text" sx={{ width: "90px", mx: 1 }} />
          ) : (
            <Typography
              variant="bodySecondaryReg"
              sx={{ color: "shades.neutral.500", pl: 1.5, pr: 0.5 }}
            >
              <Bold sx={{ color: "shades.neutral.600" }}>
                {firstItemNumber}—{lastItemNumber}
              </Bold>{" "}
              of {numItems}
            </Typography>
          )}
        </Row>
        <ArrowButton
          direction="left"
          disabled={loading || !hasPrevious}
          onClick={goToPreviousPage}
        />
        <ArrowButton
          direction="right"
          disabled={loading || !hasNext}
          onClick={goToNextPage}
        />
      </PaginatorButtonContainer>
      {showPageSizeSelector && (
        <Stack
          justifyContent="center"
          sx={{ display: { xxs: "none", lg: "flex" } }}
        >
          <PageSizeSelector onClick={onClick} sx={{ height: `${height}px` }}>
            <Typography
              variant="bodySecondaryReg"
              color="shades.neutral.500"
              paddingLeft={0.5}
            >
              Show <Bold sx={{ color: "shades.neutral.600" }}>{pageSize}</Bold>
            </Typography>
          </PageSizeSelector>
          <Menu
            id="reports-pagination-menu"
            anchorEl={anchorElement}
            open={open}
            onClose={onClose}
            disablePadding
            sx={{ "& .MuiPaper-root": { py: 1 } }}
          >
            {pageSizes.map((size) => (
              <DropdownMenuItem
                key={size}
                onClick={handlePageSizeChange(size)}
                selected={size === pageSize}
                label={
                  <Typography
                    variant="bodySecondaryMed"
                    color="shades.neutral.600"
                  >
                    {size}
                  </Typography>
                }
              />
            ))}
          </Menu>
        </Stack>
      )}
    </Row>
  );
};

export default TablePaginator;
